import { Project, CanvasComponent, ProjectSettings } from '@/types';
import JSZip from 'jszip';

export function generateHtmlCssJs(
  components: CanvasComponent[],
  settings: ProjectSettings
): {
  html: string;
  css: string;
  js: string;
} {
  let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${settings.name}</title>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
  <div class="nui-container">\n`;

  let css = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --background: 0 0% 100%;
  --foreground: 0 0% 3.9%;
  --primary: 0 0% 9%;
  --primary-foreground: 0 0% 98%;
  --card: 0 0% 100%;
  --card-foreground: 0 0% 3.9%;
  --border: 0 0% 89.8%;
  --ring: 0 0% 3.9%;
  --radius: 0.5rem;
}

body {
  font-family: 'Inter', system-ui, sans-serif;
  display: none; /* Hidden by default */
  background-color: transparent;
}

.nui-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.nui-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius);
  font-weight: 500;
  padding: 0.5rem 1rem;
  background-color: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
}

.nui-button:hover {
  opacity: 0.9;
}

.nui-input {
  width: 100%;
  border-radius: var(--radius);
  border: 1px solid hsl(var(--border));
  padding: 0.5rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.nui-input:focus {
  outline: none;
  border-color: hsl(var(--ring));
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.1);
}

.nui-card {
  background-color: hsl(var(--card));
  color: hsl(var(--card-foreground));
  border-radius: var(--radius);
  border: 1px solid hsl(var(--border));
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.nui-panel {
  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  padding: 1rem;
}\n`;

  components.forEach((component) => {
    const elementId = component.properties.name;
    css += `
#${elementId} {
  position: absolute;
  left: ${component.position.x}px;
  top: ${component.position.y}px;
  width: ${component.size.width}px;
  height: ${component.size.height}px;
  z-index: ${component.properties.zIndex || 0};
  opacity: ${(component.properties.opacity ?? 100) / 100};
  ${component.properties.styles?.backgroundColor ? `background-color: ${component.properties.styles.backgroundColor};` : ''}
  ${component.properties.styles?.borderRadius ? `border-radius: ${component.properties.styles.borderRadius}px;` : ''}
  ${component.properties.styles?.borderWidth ? `border-width: ${component.properties.styles.borderWidth}px;` : ''}
  ${component.properties.styles?.borderColor ? `border-color: ${component.properties.styles.borderColor};` : ''}
  ${component.properties.styles?.borderWidth ? 'border-style: solid;' : ''}
  ${component.properties.styles?.padding ? `padding: ${component.properties.styles.padding}px;` : ''}
  ${component.properties.styles?.textAlign ? `text-align: ${component.properties.styles.textAlign};` : ''}
  ${component.properties.styles?.fontSize ? `font-size: ${component.properties.styles.fontSize}px;` : ''}
  ${component.properties.styles?.fontColor ? `color: ${component.properties.styles.fontColor};` : ''}
  ${component.properties.styles?.fontStyles?.bold ? 'font-weight: bold;' : ''}
  ${component.properties.styles?.fontStyles?.italic ? 'font-style: italic;' : ''}
  ${component.properties.styles?.fontStyles?.underline ? 'text-decoration: underline;' : ''}
}\n`;

    // Generate HTML
    switch (component.type) {
      case 'button':
        html += `    <button id="${elementId}" class="nui-button">${component.properties.text}</button>\n`;
        break;
      case 'input':
        html += `    <input id="${elementId}" type="text" class="nui-input" placeholder="${component.properties.text}" />\n`;
        break;
      case 'text':
        html += `    <p id="${elementId}" class="nui-text">${component.properties.text}</p>\n`;
        break;
      case 'image':
        html += `    <img id="${elementId}" src="${component.properties.src}" class="nui-image" alt="${component.properties.text}" />\n`;
        break;
      case 'card':
        html += `    <div id="${elementId}" class="nui-card">${component.properties.text}</div>\n`;
        break;
      case 'list':
        html += `    <ul id="${elementId}" class="nui-list"><li>${component.properties.text}</li></ul>\n`;
        break;
      case 'panel':
        html += `    <div id="${elementId}" class="nui-panel">${component.properties.text}</div>\n`;
        break;
    }
  });

  html += `  </div>
  <script src="script.js"></script>
</body>
</html>`;

  let js = `// FiveM NUI Event Handlers
window.addEventListener('message', function(event) {
  if (event.data.type === 'show') {
    document.body.style.display = 'block';
  } else if (event.data.type === 'hide') {
    document.body.style.display = 'none';
  }
});

// Close NUI on Escape key
document.addEventListener('keyup', function(event) {
  if (event.key === 'Escape') {
    fetch('https://${settings.nickname}/close', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
  }
});\n\n`;

  // Add component event handlers
  components.forEach((component) => {
    if (component.events.onClick) {
      js += `document.getElementById('${component.properties.name}').addEventListener('click', function() {
  ${component.events.onClick}
});\n`;
    }
    if (component.events.onHover) {
      js += `document.getElementById('${component.properties.name}').addEventListener('mouseenter', function() {
  ${component.events.onHover}
});\n`;
    }
    if (component.events.onDrag) {
      js += `document.getElementById('${component.properties.name}').addEventListener('dragstart', function() {
  ${component.events.onDrag}
});\n`;
    }
  });

  return { html, css, js };
}

export async function exportToZip(
  components: CanvasComponent[],
  settings: ProjectSettings
): Promise<Blob> {
  const zip = new JSZip();
  const { html, css, js } = generateHtmlCssJs(components, settings);

  // Create nui folder
  const nuiFolder = zip.folder('nui');
  if (nuiFolder) {
    nuiFolder.file('index.html', html);
    nuiFolder.file('styles.css', css);
    nuiFolder.file('script.js', js);
  }

  // Add fxmanifest.lua
  const fxmanifest = `fx_version 'cerulean'
game 'gta5'

author '${settings.author}'
description '${settings.name}'
version '1.0.0'

ui_page 'nui/index.html'

files {
  'nui/index.html',
  'nui/styles.css',
  'nui/script.js'
}

client_scripts {
  'client.lua'
}`;

  // Add client.lua with example code
  const clientLua = `-- Local variable to track NUI state
local isNuiOpen = false

-- Command to toggle NUI
RegisterCommand('${settings.nickname}', function()
    isNuiOpen = not isNuiOpen
    if isNuiOpen then
        ShowNui()
    else
        HideNui()
    end
end)

-- Function to show NUI
function ShowNui()
    isNuiOpen = true
    SetNuiFocus(true, true)
    SendNUIMessage({
        type = "show"
    })
end

-- Function to hide NUI
function HideNui()
    isNuiOpen = false
    SetNuiFocus(false, false)
    SendNUIMessage({
        type = "hide"
    })
end

-- NUI Callback for closing the interface
RegisterNUICallback('close', function(data, cb)
    HideNui()
    cb('ok')
end)

-- Example event to show NUI from other resources
RegisterNetEvent('${settings.nickname}:showNui')
AddEventHandler('${settings.nickname}:showNui', function()
    ShowNui()
end)

-- Example event to hide NUI from other resources
RegisterNetEvent('${settings.nickname}:hideNui')
AddEventHandler('${settings.nickname}:hideNui', function()
    HideNui()
end)`;

  zip.file('fxmanifest.lua', fxmanifest);
  zip.file('client.lua', clientLua);

  // Add README with instructions
  const readme = `# ${settings.name}

This package contains a complete FiveM resource with NUI interface.

## Installation
1. Create a new folder in your FiveM resources directory
2. Extract all files into the new folder
3. Add the resource to your server.cfg: \`ensure ${settings.nickname}\`

## Usage

### In-Game Commands
- \`/${settings.nickname}\` - Toggle the NUI interface
- Press ESC to close the interface

### Triggering from Other Resources
\`\`\`lua
-- Show NUI
TriggerEvent('${settings.nickname}:showNui')

-- Hide NUI
TriggerEvent('${settings.nickname}:hideNui')
\`\`\`

### File Structure
- nui/
  - index.html
  - styles.css
  - script.js
- fxmanifest.lua
- client.lua

### Customization
- Edit client.lua to modify commands and add additional functionality
- Modify the NUI files in the nui/ directory to change the interface
- Update fxmanifest.lua if you add new files or dependencies
`;

  zip.file('README.md', readme);

  // Generate the zip file
  return await zip.generateAsync({ type: 'blob' });
}

export function saveProjectToJson(project: Project): string {
  return JSON.stringify(project, null, 2);
}

export function loadProjectFromJson(json: string): Project {
  try {
    return JSON.parse(json);
  } catch (error) {
    console.error('Failed to parse project JSON:', error);
    return {
      settings: {
        name: 'FiveM UI Project',
        author: 'Anonymous',
        nickname: 'fivem-ui',
      },
      components: [],
    };
  }
}